/**
 * The click behavior class.
 * 
 * Handles functionality for the Supernav's click behaviors.
 */

/* Dependencies --------------------------------------------- */

import { tabbable } from 'tabbable'

/* Main class ----------------------------------------------- */

function singleton( config ) {

    /** Setup --------------------------- */

    /**
     * The return object.
	 * 
     * Tracks stuff in the old-school way of doing class-like structures.
	 * Believe it or not, I didn't want to do half of the codebase like this and the other half a different way.
     */
    let object = { config: config }

	/** The state ----------------------- */

	object.state = {
		activeModal       : null,
		activeModalTrigger: null,
		clickBehaviorHooks: {}
	}

    /** Events -------------------------- */

    /**
     * Wires up event listeners.
     */
    object.listen = function () {
		jQuery( object.config.eventDelegatorSelector ).on(
            'click',
            object.config.linkSelector,
            object.handleLinkClick
        )        
    }

    /** Handlers ------------------------ */

	/**
     * Handles all link clicks.
	 * Exact behavior depends on click behavior attributes.
     */
	object.handleLinkClick = function( event ) {

		const rawLink = event.target
		const link = jQuery( rawLink );
		const clickBehavior = link.data( object.config.clickBehaviorAttribute )
		const uId = link.data( object.config.uIdAttribute )

		let hasMethod = false
		let method    = () => {}
		let args      = []

		switch ( clickBehavior ) {

			case object.config.clipboardCopyBehaviorValue:
				hasMethod = true
				method    = object.copyTextToClipboard
				args      = [ link.data( object.config.clipboardCopyTextAttribute ) ]
				break

			case object.config.modalOpenBehaviorValue:
				hasMethod = true
				method    = object.openModal
				args      = [ rawLink, link.attr( 'aria-controls' ) ]
				break
		}

		if ( uId in object.state.clickBehaviorHooks && typeof object.state.clickBehaviorHooks[uId] === 'function' ) {
			
			const hookResult = object.state.clickBehaviorHooks[uId]( {
				ogLink                  : rawLink,
				ogClickBehavior         : clickBehavior,
				ogClickBehaviorMethod   : method,
				ogClickBehaviorArguments: args
			} )

			if ( hookResult === false ) {
				event.preventDefault()
				
				// Still close any open modals even if hook returns false
				if ( object.state.activeModal ) {
					object.closeModal()
				}
				
				return
			}
		}

		if ( ! hasMethod ) return

		event.preventDefault()
		method( ...args )
	}

	/**
     * Handles keydown on focusable elements within modals.
     * Bridges several different functions that handle accessability.
     */
	 object.handleModalKeydown = function( event ) {

        switch ( event.key ) {
            case 'Tab':
                object.handleModalTab( event )
                break
            case 'Escape':
                object.handleModalEscape( event )
                break
        }
    }

	/**
	 * Handles tabbing through elements within modals.
	 */
	object.handleModalTab = function( event ) {

		const element = jQuery( event.target )
		const modal = element.closest( object.config.modalSelector )

		const tabbables = jQuery( tabbable( modal.get( 0 ) ) );

		const isFirst = element.is( tabbables.first() )
		const isLast = element.is( tabbables.last() )
		const isTabbingBackwards = event.shiftKey

		if ( isFirst && isTabbingBackwards ) {
			event.preventDefault();
			tabbables.last().focus()
		}
		else if ( isLast && ! isTabbingBackwards ) {
			event.preventDefault();
			tabbables.first().focus()
		}
	}

	/**
	 * Handles escaping from a modal.
	 */
	object.handleModalEscape = function( event ) {
		event.preventDefault()
		object.closeModal()
	}

	/**
	 * Handles clicking a modal close button.
	 */
	object.handleModalCloseClick = function() {
		object.closeModal()
	}

	/**
	 * Handles clicking outside of a modal.
	 */
	object.handleModalClickOutside = function( event ) {

		const element = jQuery( event.target )
		const closestActiveModal = element.closest( `#${object.state.activeModal}` )
		
		const clickedLink = element.closest( object.config.linkSelector )
		const isModalTrigger = clickedLink.length && 
							clickedLink.data( object.config.clickBehaviorAttribute ) === object.config.modalOpenBehaviorValue
		
		if ( closestActiveModal.length || isModalTrigger ) {
			return
		}
		
		object.closeModal()
	}

    /** Main methods -------------------- */

	/**
	 * Adds a click behavior hook given a menu item's unique ID and a callback.
	 * 
	 * The callback receives a payload object with the following parameters:
	 * 
	 * @param {Node}     ogLink                   - The link that was clicked.
	 * @param {string}   ogClickBehavior          - The type of click behavior originally assigned to the item.
	 * @param {function} ogClickBehaviorMethod    - The method originally to be executed given the item's click behavior.
	 * @param {array}    ogClickBehaviorArguments - The original arguments to be passed to the click behavior method.
	 */
	object.addHook = function( uId, callback ) {
		object.state.clickBehaviorHooks[uId] = callback
	}

	/**
	 * Copies given text to a user's clipboard.
	 */
	object.copyTextToClipboard = function( text ) {

		if ( ! navigator.clipboard || ! window.isSecureContext ) {
			object.displaySnackbar( object.config.clipboardCopyErrorMessage )
			return
		}

		navigator.clipboard.writeText( text ).then(
			() => object.displaySnackbar( `Copied "${text}" to clipboard` ),
			() => object.displaySnackbar( object.config.clipboardCopyErrorMessage )
		)	
	}

	/**
	 * Opens a modal based on an ID.
	 */
	object.openModal = function( trigger, id ) {

		trigger = jQuery( trigger )
		const modal = jQuery( `#${id}` )

		if ( ! trigger.length || ! modal.length ) return
		if ( object.state.activeModal !== id ) object.closeModal( false )

		object.state.activeModal = id
		object.state.activeModalTrigger = trigger

		object.bindModalEvents()
		modal.addClass( object.config.isActiveClass )
		trigger.attr( 'aria-expanded', true )

		// Slight timeout makes sure the first tabbable element is actually tabbable

		window.setTimeout(
			() => { jQuery( tabbable( modal.get( 0 ) ) ).first().focus() },
			30
		)
	}

	/**
	 * Closes the active modal.
	 */
	object.closeModal = function( focusTrigger = true ) {

		const activeModal = jQuery( `#${object.state.activeModal}` )
		if ( ! activeModal.length ) return

		object.unbindModalEvents()
		activeModal.removeClass( object.config.isActiveClass )

		if ( object.state.activeModalTrigger ) {

			const activeModalTrigger = jQuery( object.state.activeModalTrigger )
			activeModalTrigger.attr( 'aria-expanded', false )

			if ( focusTrigger ) {
				activeModalTrigger.focus()
			}
		}
		object.state.activeModal = null
		object.state.activeModalTrigger = null
	}

	/** Secondary methods --------------- */

	/**
	 * Initializes events pertaining to modals.
	 */
	object.bindModalEvents = function() {

		jQuery( object.config.eventDelegatorSelector ).on(
            'keydown',
            object.config.modalSelector,
            object.handleModalKeydown
        )
		jQuery( object.config.eventDelegatorSelector ).on(
            'click',
            object.config.modalCloseSelector,
            object.handleModalCloseClick
        )
		jQuery( object.config.eventDelegatorSelector ).on(
            'click',
            object.handleModalClickOutside
        )
	}

	/**
	 * Cleans up events pertaining to modals.
	 */
	object.unbindModalEvents = function() {

		jQuery( object.config.eventDelegatorSelector ).off(
            'keydown',
            object.config.modalSelector,
            object.handleModalKeydown
        )
		jQuery( object.config.eventDelegatorSelector ).off(
            'click',
            object.config.modalCloseSelector,
            object.handleModalCloseClick
		)
		jQuery( object.config.eventDelegatorSelector ).off(
            'click',
            object.handleModalClickOutside
        )
	}

	/**
	 * Displays a snackbar with given text.
	 */
	object.displaySnackbar = function( text ) {

		const snackbar = jQuery( object.config.snackbarSelector )
		snackbar.html( text )

		snackbar.addClass( object.config.isActiveClass )
		window.setTimeout(
			() => { snackbar.removeClass( object.config.isActiveClass) },
			object.config.snackbarDuration
		)
	}

    /** Helpers ------------------------- */

    /** Initialization ------------------ */

    object.listen()
    return object
}

/** Export module ------------------------------------------- */

export default {

    init( config = {} ) {

		if ( ! config.namespace ) return

        let elements = document.querySelectorAll( `.js-${config.namespace}` )

        if ( ! elements.length || ! jQuery ) return

        config = Object.assign(
            {
				
                // General config

				clickBehaviorAttribute    : `${config.namespace}-click-behavior`,
				clipboardCopyBehaviorValue: 'copy-to-clipboard',
				clipboardCopyErrorMassage : 'Error copying to clipboard',
				clipboardCopyTextAttribute: `${config.namespace}-clipboard-copy-text`,
				defaultBehaviorValue      : 'default',
				delegatorSelector         : 'body',
				hamburgerBreakpoint       : 1024,
				modalOpenBehaviorValue    : 'open-modal',
				clipboardCopyTextAttribute: `${config.namespace}-clipboard-copy-text`,
				snackbarDuration          : 4000,
				uIdAttribute              : `${config.namespace}-uid`,

				// Selectors

				eventDelegatorSelector: 'body',
				linkSelector          : `.js-${config.namespace}__menu-item-link`,
				modalSelector         : `.js-${config.namespace}__modal`,
				modalCloseSelector    : `.js-${config.namespace}__modal-close`,
				snackbarSelector      : `.js-${config.namespace}__snackbar`,

				// State classes

                isActiveClass: `is-${config.namespace}-active`
            },
            config
        )

        return singleton( config )
    }
}
